import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { UpdateRiskAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

type UpdateRiskAssessmentResultInput = {
  Id: string;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: Record<string, unknown> | null;
  AssessmentId?: string | null;
  OriginalTimestamp: string;
  ParentRiskId?: string | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (): UpdateRiskAssessmentResultMutation => ({
  updateChildRiskAssessmentResult: {
    __typename: 'GenericMutationOutput',
    affected_rows: 1,
  },
});

export const useUpdateRiskAssessmentResultTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.riskAssessmentResult.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.resultsRegister.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.riskAssessmentResultsByRiskId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.scores.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskScoresByRiskId.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [trpcEnabled, mutation.error, addNotification]);

  return {
    updateRiskAssessmentResult: async (
      variables: UpdateRiskAssessmentResultInput
    ): Promise<UpdateRiskAssessmentResultMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
